import { createFileRoute } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { useTranslation } from "react-i18next";
import { MarketingLayout, I18nPageHero } from "@/components/marketing/Layout";

export const Route = createFileRoute("/subprocessors")({
  head: () =>
    buildSeo({
      path: "/subprocessors",
      title: "Sub-processors — Reality Twin Network",
      description:
        "The third-party service providers Reality Twin uses to operate the platform, with purpose and region for each.",
    }),
  component: SubprocessorsPage,
});

type Row = { name: string; purpose: string; region: string };

// Provider names are proper nouns and stay untranslated; the purpose and
// region prose is localized via i18n (routes.subprocessors.rows, index-aligned).
const PROVIDER_NAMES = [
  "Lovable (Hosting & AI Gateway)",
  "Supabase",
  "Stripe",
  "Cloudflare",
  "Resend",
] as const;

function SubprocessorsPage() {
  const { t } = useTranslation();
  const rows = t("routes.subprocessors.rows", { returnObjects: true }) as {
    purpose: string;
    region: string;
  }[];
  const ROWS: Row[] = PROVIDER_NAMES.map((name, i) => ({
    name,
    purpose: rows[i]?.purpose ?? "",
    region: rows[i]?.region ?? "",
  }));
  return (
    <MarketingLayout>
      <I18nPageHero tKey="pages.subprocessors" />

      <div className="mx-auto max-w-3xl px-6 pt-20 text-[15.5px] leading-[1.7] text-muted-foreground">
        <p
          className="[&_a]:underline [&_a]:text-foreground"
          dangerouslySetInnerHTML={{ __html: t("routes.subprocessors.intro1") }}
        />
        <p className="mt-4">{t("routes.subprocessors.intro2")}</p>
      </div>

      <div className="mx-auto max-w-3xl px-6 py-10">
        {/* A three-column table of prose can't shrink to phone width — let it
            scroll horizontally instead of squeezing every word onto its own line. */}
        <div className="hairline rounded-xl overflow-x-auto">
          <table className="w-full text-[14px] min-w-[560px]">
            <thead className="bg-cream">
              <tr className="border-b border-border">
                <th className="text-left font-medium px-5 py-3">
                  {t("routes.subprocessors.thName")}
                </th>
                <th className="text-left font-medium px-5 py-3">
                  {t("routes.subprocessors.thPurpose")}
                </th>
                <th className="text-left font-medium px-5 py-3">
                  {t("routes.subprocessors.thRegion")}
                </th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.name} className="border-b border-border last:border-b-0 align-top">
                  <td className="px-5 py-3 font-medium">{r.name}</td>
                  <td className="px-5 py-3 text-muted-foreground">{r.purpose}</td>
                  <td className="px-5 py-3 text-muted-foreground">{r.region}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-6 pb-20 text-[15.5px] leading-[1.7] text-muted-foreground">
        <h2 className="font-serif text-[28px] text-foreground mb-3 tracking-[-0.025em]">
          {t("routes.subprocessors.changesTitle")}
        </h2>
        <p
          className="[&_a]:underline [&_a]:text-foreground"
          dangerouslySetInnerHTML={{ __html: t("routes.subprocessors.changesBody") }}
        />
      </div>
    </MarketingLayout>
  );
}
